/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { Box, Button } from '@chakra-ui/react';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import ReactExport from 'react-export-excel';
import moment from 'moment';
import useStore from '../../../../store';
import c from '../../../../constant';
import { toFormatCurrency } from '../../../../utils/format';
import { fileNameSummarizedReport } from '../../../../components/MarketDevelopment/Miscellaneous/export';

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

export default function ExcelSummarizedReportGLM({ tab, year, month, AsOf = null, During = null, By = null, ...props }) {
	const { t } = useTranslation();
	const [data, setData] = useState([]);
	const { currentLang: lang } = useStore((state) => state.currentLang);

	function sheetTitle(tab) {
		switch (tab) {
			case 'General':
				return `${lang === 'kh' ? 'ទូទៅ' : 'GI'}`;
			case 'Life':
				return `${lang === 'kh' ? 'អាយុជីវិត' : 'LI'}`;
			case 'Micro':
				return `${lang === 'kh' ? 'ខ្នាតតូច' : 'MI'}`;
			case 'GI include MI':
				return `${lang === 'kh' ? 'ទូទៅជាមួយខ្នាតតូច' : 'GI_include_MI'}`;
			case 'LI include MI':
				return `${lang === 'kh' ? 'អាយុជីវិតជាមួយខ្នាតតូច' : 'LI_include_MI'}`;
			default:
				return '';
		}
	}

	function formatCellValue(description, value) {
		const numericValue = Number(value) || 0;

		if (description === t('Number of Policies')) {
			return toFormatCurrency(numericValue, null, 0);
		}

		return toFormatCurrency(numericValue);
	}

	function makeRowAsOfData(description, values = []) {
		const data = Array.isArray(values) ? values : [];
		const monthlyData = data.slice(0, month + 1);
		const row = { description };

		monthlyData?.forEach((item, index) => {
			row[`month_${index}`] = formatCellValue(description, item?.sum || 0);
		});

		const totalValue = monthlyData?.reduce((sum, item) => {
			return sum + (Number(item?.sum) || 0);
		}, 0);
		row.total = formatCellValue(description, totalValue);

		return row;
	}

	function makeRowDuringData(description, values = {}) {
		const data = values || {};

		return {
			description,
			previousYear: formatCellValue(description, data?.[year - 1] ?? 0),
			currentYear: formatCellValue(description, data?.[year] ?? 0),
			comparison: data?.comparison ?? '0%',
		};
	}

	function makeByData(description, yearValue, values = []) {
		const data = Array.isArray(values) ? values : [];
		const row = {
			description,
			year: yearValue,
		};

		data?.forEach((item, index) => {
			const value = item?.sum || item || 0;
			row[`productName_${index}`] = formatCellValue(description, value);
		});

		return row;
	}

	function buildAsOfDataSheet(items, sheetName) {
		if (!items?.asOf?.data) return null;

		const rows = [
			makeRowAsOfData(t('Gross Premium'), items?.asOf?.data?.grosspremium),
			makeRowAsOfData(t('Number of Policies'), items?.asOf?.data?.numpolicies),
			makeRowAsOfData(t('Sum Insured'), items?.asOf?.data?.suminsured),
			makeRowAsOfData(t('Gross Claim Incurred'), items?.asOf?.data?.claimincurred),
			makeRowAsOfData(t('Gross Claim Paid'), items?.asOf?.data?.claimpaid),
		];

		return {
			type: 'asOf',
			name: sheetName,
			data: rows,
		};
	}

	function buildDuringDataSheet(items, sheetName) {
		if (!items?.during) return null;

		const rows = [
			makeRowDuringData(t('Gross Premium'), items?.during?.grosspremium),
			makeRowDuringData(t('Number of Policies'), items?.during?.numpolicies),
			makeRowDuringData(t('Sum Insured'), items?.during?.suminsured),
			makeRowDuringData(t('Gross Claim Incurred'), items?.during?.claimincurred),
			makeRowDuringData(t('Gross Claim Paid'), items?.during?.claimpaid),
		];

		return {
			type: 'during',
			name: sheetName,
			data: rows,
		};
	}

	function buildByDataSheet(items, sheetName) {
		if (!items?.duringByProduct) return null;
		const productTitles = Array.isArray(items?.duringByProduct?.products) ? items?.duringByProduct?.products : [];

		const rows = [
			makeByData(t('Gross Premium'), `${year - 1}`, items?.duringByProduct?.grosspremium?.[year - 1]),
			makeByData(t('Gross Premium'), `${year}`, items?.duringByProduct?.grosspremium?.[year]),

			makeByData(t('Number of Policies'), `${year - 1}`, items?.duringByProduct?.numpolicies?.[year - 1]),
			makeByData(t('Number of Policies'), `${year}`, items?.duringByProduct?.numpolicies?.[year]),

			makeByData(t('Sum Insured'), `${year - 1}`, items?.duringByProduct?.suminsured?.[year - 1]),
			makeByData(t('Sum Insured'), `${year}`, items?.duringByProduct?.suminsured?.[year]),

			makeByData(t('Gross Claim Incurred'), `${year - 1}`, items?.duringByProduct?.claimincurred?.[year - 1]),
			makeByData(t('Gross Claim Incurred'), `${year}`, items?.duringByProduct?.claimincurred?.[year]),

			makeByData(t('Gross Claim Paid'), `${year - 1}`, items?.duringByProduct?.claimpaid?.[year - 1]),
			makeByData(t('Gross Claim Paid'), `${year}`, items?.duringByProduct?.claimpaid?.[year]),
		];

		return {
			type: 'by',
			name: sheetName,
			data: rows,
			productTitles,
		};
	}

	useEffect(() => {
		const sheets = [];
		const title = sheetTitle(tab);

		if (AsOf) {
			sheets.push(
				buildAsOfDataSheet(
					AsOf,
					`${lang === 'en' ? `${title}_as_of_${moment(month + 1, 'M').format('MMMM')}_${year}` : `${title}_គិតត្រឹម_${t(moment(month + 1, 'M').format('MMMM'))}_${year}`}`,
				),
			);
		}

		if (During) {
			sheets.push(
				buildDuringDataSheet(
					During,
					`${lang === 'en' ? `${title}_in_${moment(month + 1, 'M').format('MMMM')}_${year}` : `${title}_ក្នុង_${t(moment(month + 1, 'M').format('MMMM'))}_${year}`}`,
				),
			);
		}

		if (By) {
			sheets.push(
				buildByDataSheet(
					By,
					`${lang === 'en' ? `${title}_by_product_${moment(month + 1, 'M').format('MMMM')}_${year}` : `${title}_តាមផលិតផល_${t(moment(month + 1, 'M').format('MMMM'))}_${year}`}`,
				),
			);
		}

		setData(sheets.filter(Boolean));
	}, [tab, AsOf, During, By, lang, year, month]);

	return (
		<Box {...props}>
			<ExcelFile
				filename={fileNameSummarizedReport(month + 1, year)}
				element={
					<Button
						leftIcon={<FontAwesomeIcon icon={faFileExcel} style={{ fontSize: 16 }} />}
						colorScheme="gray"
						variant="solid"
						size="sm"
						borderRadius={c.borderRadius}
						border="1px solid #bdc3c7"
					>
						{t('Export')}
					</Button>
				}
			>
				{data?.map((el, i) => {
					if (el?.type === 'asOf') {
						return (
							<ExcelSheet key={`sheet-insurance-${i}`} data={el?.data} name={t(el?.name)}>
								<ExcelColumn label={t('Description')} value="description" />
								{Array.from({ length: month + 1 }, (_, m) => (
									<ExcelColumn label={t(moment(m + 1, 'M').format('MMMM'))} value={`month_${m}`} />
								))}
								<ExcelColumn label={t('Total')} value="total" />
							</ExcelSheet>
						);
					}
					if (el?.type === 'during') {
						return (
							<ExcelSheet key={`sheet-insurance-${i}`} data={el?.data} name={t(el?.name)}>
								<ExcelColumn label={t('Description')} value="description" />
								<ExcelColumn label={`${year - 1}`} value="previousYear" />
								<ExcelColumn label={`${year}`} value="currentYear" />
								<ExcelColumn label={t('Comparison')} value="comparison" />
							</ExcelSheet>
						);
					}

					if (el?.type === 'by') {
						return (
							<ExcelSheet key={`sheet-comparison-${i}`} data={el?.data || []} name={t(el?.name)}>
								<ExcelColumn label={t('Description')} value="description" />
								<ExcelColumn label={t('Year')} value="year" />
								{el?.productTitles?.map((product, index) => {
									return (
										<ExcelColumn
											key={`product-col-${index}`}
											label={lang === 'kh' ? product?.productNameKh : product?.productNameEn}
											value={`productName_${index}`}
										/>
									);
								})}
							</ExcelSheet>
						);
					}
					return null;
				})}
			</ExcelFile>
		</Box>
	);
}
